import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useRegistrationStatus, RegistrationClosed, ModalityPicker, ReturningBanner } from "@/components/company/RegistrationGate";
import { toast } from "sonner";
import { Loader2, Landmark, Eye, ArrowLeft, LogIn } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SiteHeader, PageFooter } from "@/components/brand/Brand";
import { FormSection, Field } from "@/components/registro/RegistroParts";
import { BrandSection, OffersEditor, NEW_OFFER } from "@/components/company/InscripcionParts";
import { ProfilePreview } from "@/components/company/ProfileView";
import { SelectField } from "@/pages/Registro";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { FullLoader } from "@/components/auth/RequireRole";
import { useI18n } from "@/lib/i18n/i18n";
import { TITULACION_LIST } from "@/lib/demo/seed";
import { BANK } from "@/lib/demo/applications";

const SECTORS = ["Tecnología e ingeniería", "Industria y energía", "Automoción", "Consultoría", "Energía", "Consultoría tecnológica", "Retail tech"];
const ROLES = ["admin", "recruiter", "recruiter"];
const EMPTY = {
  name: "", cif: "", sector: "", web: "", address: "", contactName: "", contactEmail: "", contactPhone: "", description: "",
  logoImg: null, cover: null, brandColor: "#8B0000",
  titulaciones: [], offers: [{ ...NEW_OFFER, type: "Prácticas" }], users: ROLES.map((role) => ({ name: "", email: "", role })),
};
const REQUIRED = ["name", "cif", "sector", "contactName", "contactEmail", "description"];
const pick = (f) => ({ ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] ?? EMPTY[k]])), offers: f.offers.filter((o) => o.title.trim()) });

const CompanyAccess = () => {
  const { t } = useI18n();
  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5" data-testid="company-access-form">
      <p className="text-sm font-bold">{t("¿Ya has inscrito tu empresa?")}</p>
      <p className="mt-1 text-xs text-slate-500">{t("Accede con el email y la contraseña del usuario administrador.")}</p>
      <Link to="/login" className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-slate-950 text-sm font-bold text-white transition-opacity hover:opacity-90" data-testid="company-access-button"><LogIn className="h-4 w-4" /> {t("Acceder")}</Link>
    </div>
  );
};

export default function EmpresaInscripcion() {
  const nav = useNavigate();
  const { appId } = useParams();
  const { user, startSession } = useAuth();
  const { t } = useI18n();
  const [editing, setEditing] = useState(appId ? undefined : null);
  const [form, setForm] = useState(EMPTY);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(false);
  const [pw, setPw] = useState({ password: "", password2: "" });
  const [params] = useSearchParams();
  const renew = params.get("renovar") === "1" && user?.role === "company" && user.companyRole === "admin" && !!user.companyId;
  const reg = useRegistrationStatus();
  const [modality, setModality] = useState(null);
  const [returning, setReturning] = useState(null);
  useEffect(() => {
    if (!renew) return;
    api.get("/applications/renewal").then((r) => setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(r.data.prefill).filter(([, v]) => v != null)) }))).catch(() => {});
  }, [renew]);
  const lookup = async () => {
    if (renew || editing || form.cif.trim().length < 8) return;
    try { const { data } = await api.post("/applications/lookup", { cif: form.cif }); setReturning(data); } catch { setReturning(null); }
  };
  useEffect(() => {
    if (!appId) return;
    api.get("/applications/mine").then((r) => { if (r.data.id !== appId) { setEditing(null); return; } setEditing(r.data); setForm({ ...EMPTY, ...r.data }); }).catch(() => setEditing(null));
  }, [appId]);
  if (appId && editing === undefined) return <FullLoader />;
  if (appId && !editing) return <Navigate to="/empresas/inscripcion" replace />;
  if (!appId && !renew && user?.role === "company" && user.applicationId) return <Navigate to={`/empresas/inscripcion/${user.applicationId}`} replace />;
  if (!appId && reg === undefined) return <FullLoader />;
  if (!appId && reg && !reg.open) return <RegistrationClosed s={reg} />;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const isNew = !editing && !renew;
  const setList = (k, i, patch) => setForm((f) => ({ ...f, [k]: f[k].map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
  const toggleTit = (x) => setForm((f) => ({ ...f, titulaciones: f.titulaciones.includes(x) ? f.titulaciones.filter((y) => y !== x) : [...f.titulaciones, x] }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    REQUIRED.forEach((k) => { if (!form[k].trim()) errs[k] = "Campo obligatorio"; });
    if (form.contactEmail && !/^\S+@\S+\.\S+$/.test(form.contactEmail)) errs.contactEmail = "Introduce un email válido";
    if (!form.titulaciones.length) errs.titulaciones = "Selecciona al menos una titulación";
    if (reg?.modalities?.length && !editing && !modality) errs.modalityId = "Elige una modalidad";
    if (isNew && (!form.users[0].name.trim() || !form.users[0].email.trim())) errs.users = "Indica al menos el usuario administrador";
    if (!editing && !consent) errs.consent = "Debes aceptar las condiciones para completar el registro";
    if (isNew && (pw.password.length < 8 || !/\d/.test(pw.password) || !/[a-zA-Z]/.test(pw.password))) errs.password = "Mínimo 8 caracteres, con letras y números.";
    if (isNew && pw.password !== pw.password2) errs.password = "Las contraseñas no coinciden";
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error(t("Revisa los campos marcados")); return; }
    setSending(true);
    try {
      if (editing) {
        await api.put("/applications/mine", pick(form));
        toast.success(t("Perfil actualizado"));
        nav(`/empresas/inscripcion/${editing.id}`);
        return;
      }
      if (renew) {
        const { data } = await api.post("/applications/renewal", { ...pick(form), modalityId: modality, consent: true });
        if (data.debtorMessage) toast.warning(t(data.debtorMessage), { duration: 12000 });
        toast.success(t("Solicitud enviada correctamente"));
        window.location.assign(`/empresas/inscripcion/${data.application.id}`);
        return;
      }
      const { data } = await api.post("/applications", { ...pick(form), modalityId: modality, users: form.users, password: pw.password, consent: true });
      startSession(data);
      if (data.debtorMessage) toast.warning(t(data.debtorMessage), { duration: 12000 });
      toast.success(t("Solicitud enviada correctamente"));
      nav(`/empresas/inscripcion/${data.application.id}`);
    } catch (err) {
      if (err?.response?.data?.detail?.code?.startsWith?.("registration_")) { window.location.reload(); return; }
      const { message, fields } = apiError(err);
      setErrors(fields);
      toast.error(t(message));
      setSending(false);
    }
  };

  const Err = ({ k }) => (errors[k] ? <p className="mt-2 text-xs font-medium text-rose-600" data-testid={`company-reg-error-${k}`}>{t(errors[k])}</p> : null);
  const PreviewBtn = ({ testId, className = "" }) => (
    <button type="button" onClick={() => setPreview(true)} className={`inline-flex items-center justify-center gap-2 rounded-full border-2 border-slate-900 px-5 py-2.5 text-sm font-bold transition-colors hover:bg-slate-900 hover:text-white ${className}`} data-testid={testId}>
      <Eye className="h-4 w-4" /> {t("Vista previa")}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-registration-page">
      <SiteHeader />
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 lg:grid-cols-[280px_1fr] lg:px-10">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          {editing && <Link to={`/empresas/inscripcion/${editing.id}`} className="mb-5 inline-flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-upv" data-testid="company-edit-back-link"><ArrowLeft className="h-3.5 w-3.5" /> {t("Volver a la solicitud")}</Link>}
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Inscripción de empresas")}{editing ? ` · ${editing.ref}` : ""}</p>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight" data-testid="company-reg-title">{t(editing ? "Editar perfil público" : "Inscribe tu empresa")}</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">{t(editing ? "Actualiza la imagen, la descripción y las ofertas que ven los participantes." : "Solicitud online, pago por transferencia y aprobación por la UPV.")}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <PreviewBtn testId="company-reg-preview-button" />
          </div>
          {!editing && <CompanyAccess />}
        </aside>
        <form onSubmit={submit} noValidate className="rounded-[28px] border border-slate-200 bg-white px-6 shadow-sm sm:px-10" data-testid="company-reg-form">
          <FormSection n={1} title={t("Datos de la empresa")}>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2"><Field id="name" label="Razón social" value={form.name} onChange={set("name")} error={errors.name} /></div>
              <div onBlur={lookup}><Field id="cif" label="CIF / NIF" value={form.cif} onChange={set("cif")} error={errors.cif} /></div>
              <SelectField id="sector" label="Sector" value={form.sector} onChange={set("sector")} options={SECTORS} error={errors.sector} />
              <Field id="web" label="Web" optional value={form.web} onChange={set("web")} />
              <Field id="address" label="Dirección" optional value={form.address} onChange={set("address")} />
              <Field id="contactName" label="Persona de contacto" value={form.contactName} onChange={set("contactName")} error={errors.contactName} />
              <Field id="contactEmail" label="Email de contacto" type="email" value={form.contactEmail} onChange={set("contactEmail")} error={errors.contactEmail} />
              <Field id="contactPhone" label="Teléfono" optional value={form.contactPhone} onChange={set("contactPhone")} />
            </div>
            <ReturningBanner info={returning} onUse={() => { setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(returning.prefill || {}).filter(([, v]) => v != null)) })); setReturning(null); toast.success(t("Datos cargados: revísalos antes de enviar")); }} />
          </FormSection>
          <FormSection n={2} title={t("Imagen de marca")}>
            <BrandSection form={form} set={set} />
          </FormSection>
          <FormSection n={3} title={t("Perfil de reclutamiento")}>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{t("Descripción de la empresa")}</Label>
            <Textarea value={form.description} onChange={(e) => set("description")(e.target.value)} rows={5} className={`mt-2 rounded-xl ${errors.description ? "border-rose-400" : ""}`} data-testid="company-reg-input-description" />
            <p className="mt-1.5 text-xs text-slate-400">{t("Se muestra en «Sobre la empresa» de tu perfil público.")}</p>
            <Err k="description" />
            <Label className="mt-6 block text-xs font-semibold uppercase tracking-wider text-slate-600">{t("Titulaciones buscadas")}</Label>
            <div className="mt-3 flex flex-wrap gap-2">
              {TITULACION_LIST.map((x) => (
                <button key={x} type="button" onClick={() => toggleTit(x)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${form.titulaciones.includes(x) ? "border-upv bg-upv text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`} data-testid={`company-reg-tit-${x}`}>{t(x)}</button>
              ))}
            </div>
            <Err k="titulaciones" />
            <Label className="mt-6 block text-xs font-semibold uppercase tracking-wider text-slate-600">{t("Oportunidades de empleo")}</Label>
            <OffersEditor offers={form.offers} titulaciones={form.titulaciones} onChange={set("offers")} />
          </FormSection>
          {isNew && (
            <FormSection n={4} title={t("Usuarios de la empresa (máx. 3)")}>
              <div className="space-y-4">
                {form.users.map((u, i) => (
                  <div key={i} className="grid gap-3 rounded-2xl bg-slate-50 p-4 sm:grid-cols-[120px_1fr_1fr] sm:items-center" data-testid={`company-reg-user-${i}`}>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("Usuario {n}", { n: i + 1 })}<span className="mt-1 block normal-case tracking-normal text-upv">{t(u.role === "admin" ? "Administrador" : "Reclutador")}</span></span>
                    <Input value={u.name} onChange={(e) => setList("users", i, { name: e.target.value })} placeholder={t("Nombre")} className="h-11 rounded-xl bg-white" data-testid={`company-reg-user-name-${i}`} />
                    <Input value={u.email} onChange={(e) => setList("users", i, { email: e.target.value })} placeholder={t("Email")} type="email" className="h-11 rounded-xl bg-white" data-testid={`company-reg-user-email-${i}`} />
                  </div>
                ))}
              </div>
              <Err k="users" />
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <Field id="admin-password" label="Contraseña del administrador" type="password" autoComplete="new-password" value={pw.password} onChange={(v) => setPw((p) => ({ ...p, password: v }))} error={errors.password} />
                <Field id="admin-password2" label="Repite la contraseña" type="password" autoComplete="new-password" value={pw.password2} onChange={(v) => setPw((p) => ({ ...p, password2: v }))} />
              </div>
              <p className="mt-2 text-xs text-slate-400">{t("El administrador accede con su email y esta contraseña. Los otros usuarios recibirán un enlace de activación tras la aprobación.")}</p>
            </FormSection>
          )}
          <FormSection n={isNew ? 5 : 4} title={t(editing ? "Publicar cambios" : "Participación y pago")}>
            {!editing && (
              <>
                <div className="flex items-start gap-4 rounded-2xl border border-slate-200 p-6" data-testid="company-reg-payment-box">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-upv-light text-upv"><Landmark className="h-5 w-5" /></span>
                  <dl className="flex-1 space-y-2 text-sm">
                    <div className="flex justify-between gap-4"><dt className="text-slate-500">{t("Cuota de participación")}</dt><dd className="text-right font-semibold">{t(BANK.amount)}</dd></div>
                    <div className="flex justify-between gap-4"><dt className="text-slate-500">{t("Método de pago")}</dt><dd className="text-right font-semibold">{t("Transferencia bancaria")}</dd></div>
                    <p className="pt-2 text-xs text-slate-400">{t("Recibirás los datos bancarios y una referencia al enviar la solicitud.")}</p>
                  </dl>
                </div>
                <ModalityPicker modalities={reg?.modalities} value={modality} onChange={setModality} error={errors.modalityId} />
                {reg?.remaining != null && <p className="mt-3 text-xs font-semibold text-slate-500" data-testid="company-reg-remaining">{t("Plazas disponibles: {n}", { n: reg.remaining })}</p>}
                <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-xl bg-slate-50 p-4">
                  <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 h-5 w-5" data-testid="company-reg-consent-checkbox" />
                  <span className="text-sm font-semibold text-slate-800">{t("Acepto las condiciones de participación de empresas en el {title}.", { title: reg?.editionTitle || "Foro de Empleo UPV" })}</span>
                </label>
                <Err k="consent" />
              </>
            )}
            <PreviewBtn testId="company-reg-preview-bottom-button" className={editing ? "w-full" : "mt-8 w-full"} />
            <button type="submit" disabled={sending} className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-upv font-display text-base font-bold tracking-wider text-white transition-colors hover:bg-upv-dark disabled:opacity-80" data-testid="company-reg-submit-button">
              {sending ? <><Loader2 className="h-5 w-5 animate-spin" /> {t(editing ? "Guardando…" : "Enviando…")}</> : t(editing ? "GUARDAR CAMBIOS" : "ENVIAR SOLICITUD")}
            </button>
          </FormSection>
        </form>
      </div>
      <ProfilePreview open={preview} onOpenChange={setPreview} data={{ ...pick(form), id: editing?.id || "preview", name: form.name || t("Tu empresa"), stand: editing?.stand }} />
      <PageFooter />
    </div>
  );
}
